import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiJson } from '../api/client.js';
import { FormInput } from '../components/FormControls.jsx';
import { useAuth } from '../context/AuthContext.jsx';
import { SearchableSelect } from '../components/SearchableSelect.jsx';

const STANDALONE_PROJECT = 'Standalone';

function formatBytes(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) {
    return '—';
  }
  if (n < 1024) {
    return `${n} B`;
  }
  if (n < 1024 * 1024) {
    return `${(n / 1024).toFixed(1)} KB`;
  }
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function containerKey(c) {
  return String(c?.id || c?.short_id || c?.name || '');
}

function isRunning(state) {
  const s = String(state || '').toLowerCase();
  return s === 'running' || s === 'online';
}

function containerProject(c) {
  const labels = c?.labels || {};
  return (
    labels['com.docker.compose.project'] ||
    labels['com.docker.compose.project.name'] ||
    c?.project ||
    STANDALONE_PROJECT
  );
}

function containerService(c) {
  const labels = c?.labels || {};
  return labels['com.docker.compose.service'] || c?.service || null;
}

function enrichContainer(c) {
  return {
    ...c,
    project: containerProject(c),
    service: containerService(c),
  };
}

function mergeLiveContainer(existing, live) {
  const prev = existing || {};
  const merged = {
    ...prev,
    id: live.id ?? prev.id,
    short_id: live.short_id ?? live.id ?? prev.short_id,
    name: live.name ?? prev.name,
    status: live.status ?? prev.status,
    state: live.state ?? live.status ?? prev.state,
    image: live.image ?? prev.image,
    pid: live.pid ?? prev.pid,
    port: live.port ?? prev.port ?? null,
    ports: Array.isArray(live.ports) && live.ports.length ? live.ports : prev.ports,
    cpu: live.cpu ?? prev.cpu,
    memory: live.memory ?? prev.memory,
    protected: live.protected ?? prev.protected,
    labels: live.labels || prev.labels || {},
  };
  return enrichContainer(merged);
}

function groupByProject(containers) {
  const map = new Map();
  for (const raw of containers) {
    const c = enrichContainer(raw);
    const project = c.project || STANDALONE_PROJECT;
    if (!map.has(project)) {
      map.set(project, []);
    }
    map.get(project).push(c);
  }

  const projects = [...map.entries()].map(([name, items]) => {
    const sorted = [...items].sort((a, b) =>
      String(a.service || a.name || '').localeCompare(String(b.service || b.name || '')),
    );
    const running = sorted.filter((c) => isRunning(c.state || c.status)).length;
    const paused = sorted.filter((c) => String(c.state || '').toLowerCase() === 'paused').length;
    const cpu = sorted.reduce((sum, c) => sum + (Number(c.cpu) || 0), 0);
    const memory = sorted.reduce((sum, c) => sum + (Number(c.memory) || 0), 0);
    let status = 'stopped';
    if (running === sorted.length && sorted.length > 0) {
      status = 'running';
    } else if (running > 0) {
      status = 'partial';
    } else if (paused > 0) {
      status = 'paused';
    }
    return {
      name,
      containers: sorted,
      total: sorted.length,
      running,
      paused,
      cpu: Math.round(cpu * 100) / 100,
      memory,
      status,
      standalone: name === STANDALONE_PROJECT,
    };
  });

  projects.sort((a, b) => {
    if (a.standalone !== b.standalone) {
      return a.standalone ? 1 : -1;
    }
    return a.name.localeCompare(b.name);
  });

  return projects;
}

function projectStatusBadge(status) {
  if (status === 'running') {
    return { label: 'Running', className: 'text-bg-success' };
  }
  if (status === 'partial') {
    return { label: 'Partial', className: 'text-bg-warning' };
  }
  if (status === 'paused') {
    return { label: 'Paused', className: 'text-bg-warning' };
  }
  return { label: 'Stopped', className: 'text-bg-secondary' };
}

const WS_STATUS = {
  idle: { label: 'Offline', className: 'text-bg-secondary' },
  connecting: { label: 'Connecting…', className: 'text-bg-warning' },
  live: { label: 'Live', className: 'text-bg-success' },
  error: { label: 'Stream error', className: 'text-bg-danger' },
};

export function AdminDockerPage() {
  const { user } = useAuth();
  const caps = user?.capabilities ?? {};
  const [containers, setContainers] = useState([]);
  const [version, setVersion] = useState(null);
  const [system, setSystem] = useState(null);
  const [metricsTs, setMetricsTs] = useState(null);
  const [wsStatus, setWsStatus] = useState('idle');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [expanded, setExpanded] = useState(() => new Set());
  const [logsFor, setLogsFor] = useState(null);
  const [logsType, setLogsType] = useState('both');
  const [logsLines, setLogsLines] = useState(100);
  const [logsContent, setLogsContent] = useState('');
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsClearing, setLogsClearing] = useState(false);

  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const refreshTimerRef = useRef(null);
  const stoppedRef = useRef(false);
  const backoffRef = useRef(1000);

  const projects = useMemo(() => groupByProject(containers), [containers]);

  const flash = useCallback((text, isError = false) => {
    if (isError) {
      setError(text);
      setMessage('');
    } else {
      setMessage(text);
      setError('');
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [list, ver] = await Promise.all([
        apiJson('/api/admin/docker/containers'),
        apiJson('/api/admin/docker/version').catch(() => null),
      ]);
      const rows = Array.isArray(list?.data) ? list.data.map(enrichContainer) : [];
      setContainers(rows);
      setVersion(ver?.data ?? null);
    } catch (e) {
      flash(e.message, true);
      setContainers([]);
    } finally {
      setLoading(false);
    }
  }, [flash]);

  const clearTimers = () => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  };

  const disconnectWs = useCallback(() => {
    clearTimers();
    if (wsRef.current) {
      try {
        wsRef.current.onopen = null;
        wsRef.current.onmessage = null;
        wsRef.current.onerror = null;
        wsRef.current.onclose = null;
        wsRef.current.close();
      } catch {
        // ignore
      }
      wsRef.current = null;
    }
  }, []);

  const connectWs = useCallback(async () => {
    if (stoppedRef.current) {
      return;
    }

    disconnectWs();
    setWsStatus('connecting');

    let credentials;
    try {
      credentials = await apiJson('/api/admin/docker/ws-credentials');
    } catch {
      setWsStatus('error');
      reconnectTimerRef.current = setTimeout(() => {
        void connectWs();
      }, backoffRef.current);
      backoffRef.current = Math.min(backoffRef.current * 2, 15000);
      return;
    }

    if (!credentials?.url || stoppedRef.current) {
      setWsStatus('error');
      return;
    }

    const ws = new WebSocket(credentials.url);
    wsRef.current = ws;

    ws.onopen = () => {
      if (stoppedRef.current) {
        ws.close();
        return;
      }
      setWsStatus('live');
      backoffRef.current = 1000;

      const expiresAt = credentials.expires_at ? Date.parse(credentials.expires_at) : 0;
      if (expiresAt > Date.now()) {
        const refreshIn = Math.max(30_000, expiresAt - Date.now() - 15_000);
        refreshTimerRef.current = setTimeout(() => {
          void connectWs();
        }, refreshIn);
      }
    };

    ws.onmessage = (event) => {
      let msg;
      try {
        msg = JSON.parse(String(event.data));
      } catch {
        return;
      }

      if (msg?.type === 'hello') {
        return;
      }

      if (msg?.type === 'error') {
        setWsStatus('error');
        return;
      }

      if (msg?.type !== 'metrics') {
        return;
      }

      setMetricsTs(msg.ts || Date.now());
      if (msg.system) {
        setSystem(msg.system);
      }

      const liveList = Array.isArray(msg.containers) ? msg.containers : [];
      setContainers((prev) => {
        const byKey = new Map();
        for (const c of prev) {
          byKey.set(containerKey(c), c);
          if (c.name) {
            byKey.set(String(c.name), c);
          }
          if (c.short_id) {
            byKey.set(String(c.short_id), c);
          }
        }
        if (liveList.length === 0) {
          return prev;
        }
        return liveList.map((live) => {
          const existing =
            byKey.get(containerKey(live)) ||
            byKey.get(String(live.name || '')) ||
            byKey.get(String(live.id || live.short_id || ''));
          return mergeLiveContainer(existing, live);
        });
      });
    };

    ws.onerror = () => {
      setWsStatus('error');
    };

    ws.onclose = () => {
      wsRef.current = null;
      if (stoppedRef.current) {
        setWsStatus('idle');
        return;
      }
      setWsStatus('connecting');
      reconnectTimerRef.current = setTimeout(() => {
        void connectWs();
      }, backoffRef.current);
      backoffRef.current = Math.min(backoffRef.current * 2, 15000);
    };
  }, [disconnectWs]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    stoppedRef.current = false;
    void connectWs();
    return () => {
      stoppedRef.current = true;
      disconnectWs();
    };
  }, [connectWs, disconnectWs]);

  const toggleProject = (name) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  };

  const runAction = async (id, action) => {
    setBusyId(`${id}:${action}`);
    setError('');
    try {
      await apiJson(`/api/admin/docker/containers/${encodeURIComponent(id)}/${action}`, {
        method: 'POST',
      });
      flash(`${action} requested for ${id}`);
      await load();
    } catch (e) {
      flash(e.message, true);
    } finally {
      setBusyId('');
    }
  };

  const runProjectAction = async (project, action) => {
    const targets = project.containers.filter((c) => {
      if (action === 'start') {
        return !isRunning(c.state || c.status);
      }
      if (c.protected && (action === 'stop' || action === 'restart')) {
        return false;
      }
      if (action === 'stop') {
        return isRunning(c.state || c.status) || String(c.state || '').toLowerCase() === 'paused';
      }
      return true;
    });

    if (targets.length === 0) {
      flash(`Nothing to ${action} in ${project.name}`, true);
      return;
    }

    setBusyId(`project:${project.name}:${action}`);
    setError('');
    const failures = [];
    for (const c of targets) {
      const id = String(c.name || c.short_id || c.id);
      try {
        await apiJson(`/api/admin/docker/containers/${encodeURIComponent(id)}/${action}`, {
          method: 'POST',
        });
      } catch (e) {
        failures.push(`${id}: ${e.message}`);
      }
    }

    if (failures.length) {
      flash(`${action} finished with errors: ${failures.join('; ')}`, true);
    } else {
      flash(`${action} requested for ${targets.length} container(s) in ${project.name}`);
    }
    await load();
    setBusyId('');
  };

  const openLogs = async (c) => {
    const id = String(c.name || c.short_id || c.id);
    setLogsFor(id);
    setLogsLoading(true);
    setLogsContent('');
    try {
      const res = await apiJson(
        `/api/admin/docker/containers/${encodeURIComponent(id)}/logs?type=${encodeURIComponent(logsType)}&lines=${logsLines}`,
      );
      const data = res?.data;
      setLogsContent(typeof data?.content === 'string' ? data.content : JSON.stringify(data ?? res, null, 2));
    } catch (e) {
      setLogsContent('');
      flash(e.message, true);
    } finally {
      setLogsLoading(false);
    }
  };

  const refreshLogs = async () => {
    if (!logsFor) {
      return;
    }
    setLogsLoading(true);
    try {
      const res = await apiJson(
        `/api/admin/docker/containers/${encodeURIComponent(logsFor)}/logs?type=${encodeURIComponent(logsType)}&lines=${logsLines}`,
      );
      const data = res?.data;
      setLogsContent(typeof data?.content === 'string' ? data.content : JSON.stringify(data ?? res, null, 2));
    } catch (e) {
      flash(e.message, true);
    } finally {
      setLogsLoading(false);
    }
  };

  const clearLogs = async (id = logsFor) => {
    if (!id) {
      return;
    }
    if (!window.confirm(`Clear Docker logs for “${id}”? This truncates the container log file and cannot be undone.`)) {
      return;
    }
    setLogsClearing(true);
    setError('');
    try {
      await apiJson(`/api/admin/docker/containers/${encodeURIComponent(id)}/logs/clear`, {
        method: 'POST',
      });
      flash(`Logs cleared for ${id}`);
      setLogsContent('');
      if (logsFor === id) {
        await refreshLogs();
      }
    } catch (e) {
      flash(e.message, true);
    } finally {
      setLogsClearing(false);
    }
  };

  const clearProjectLogs = async (project) => {
    if (!window.confirm(`Clear logs for all containers in “${project.name}”? This cannot be undone.`)) {
      return;
    }
    setLogsClearing(true);
    setError('');
    const failures = [];
    for (const c of project.containers) {
      const id = String(c.name || c.short_id || c.id);
      try {
        await apiJson(`/api/admin/docker/containers/${encodeURIComponent(id)}/logs/clear`, {
          method: 'POST',
        });
      } catch (e) {
        failures.push(`${id}: ${e.message}`);
      }
    }
    if (failures.length) {
      flash(`Clear finished with errors: ${failures.join('; ')}`, true);
    } else {
      flash(`Logs cleared for ${project.containers.length} container(s) in ${project.name}`);
    }
    if (logsFor) {
      setLogsContent('');
      await refreshLogs();
    }
    setLogsClearing(false);
  };

  if (!caps.docker) {
    return (
      <div className="page">
        <p className="err">Docker is not included in your current plan.</p>
      </div>
    );
  }

  const stream = WS_STATUS[wsStatus] || WS_STATUS.idle;
  const mem = system?.memory;

  return (
    <div className="page">
      <div className="d-flex justify-content-between align-items-start gap-2 flex-wrap">
        <div>
          <h1 style={{ marginBottom: '0.25rem' }}>Docker projects</h1>
          <p className="muted small" style={{ margin: 0 }}>
            Compose projects on your Docker host. Open a project to manage its containers.{' '}
            <Link to="/admin/integrations">Integration settings →</Link>
          </p>
          {version ? (
            <p className="muted small" style={{ marginTop: '0.35rem' }}>
              API {version.api || '—'} · Engine {version.docker || '—'}
            </p>
          ) : null}
        </div>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <span className={`badge ${stream.className}`}>{stream.label}</span>
          <button type="button" className="btn btn-outline-secondary" onClick={() => void load()} disabled={loading}>
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {error ? <p className="err">{error}</p> : null}
      {message ? <p className="ok">{message}</p> : null}

      {system ? (
        <div className="pm2-system-metrics">
          <div>
            <span className="muted small">Host CPU</span>
            <strong>{system.cpu_percent != null ? `${system.cpu_percent}%` : '—'}</strong>
          </div>
          <div>
            <span className="muted small">Memory</span>
            <strong>
              {mem ? `${formatBytes(mem.used)} / ${formatBytes(mem.total)} (${mem.used_percent ?? '—'}%)` : '—'}
            </strong>
          </div>
          <div>
            <span className="muted small">Load</span>
            <strong>
              {Array.isArray(system.loadavg) ? system.loadavg.map((n) => Number(n).toFixed(2)).join(' · ') : '—'}
            </strong>
          </div>
          <div>
            <span className="muted small">Updated</span>
            <strong>{metricsTs ? new Date(metricsTs).toLocaleTimeString() : '—'}</strong>
          </div>
        </div>
      ) : null}

      <div className="docker-projects" style={{ marginTop: '1rem' }}>
        {loading && projects.length === 0 ? <p className="muted">Loading…</p> : null}
        {!loading && projects.length === 0 ? (
          <p className="muted">No projects returned. Check the API key in Integrations.</p>
        ) : null}

        {projects.map((project) => {
          const open = expanded.has(project.name);
          const badge = projectStatusBadge(project.status);
          const projectBusy = busyId.startsWith(`project:${project.name}:`);
          return (
            <div key={project.name} className={`docker-project${open ? ' is-open' : ''}`}>
              <div className="docker-project-header">
                <button
                  type="button"
                  className="docker-project-toggle"
                  aria-expanded={open}
                  onClick={() => toggleProject(project.name)}
                >
                  <span className="docker-project-chevron" aria-hidden>
                    {open ? '▾' : '▸'}
                  </span>
                  <span className="docker-project-name">{project.name}</span>
                  <span className={`badge ${badge.className}`}>{badge.label}</span>
                  <span className="muted small">
                    {project.running}/{project.total} running
                  </span>
                </button>
                <div className="docker-project-meta">
                  <span className="muted small">CPU {project.cpu ? `${project.cpu}%` : '—'}</span>
                  <span className="muted small">Mem {formatBytes(project.memory)}</span>
                </div>
                <div className="row-actions docker-project-actions">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    disabled={projectBusy || project.status === 'running'}
                    onClick={() => void runProjectAction(project, 'start')}
                  >
                    Start
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    disabled={projectBusy || project.status === 'stopped'}
                    onClick={() => void runProjectAction(project, 'restart')}
                  >
                    Restart
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-warning"
                    disabled={projectBusy || project.status === 'stopped'}
                    onClick={() => void runProjectAction(project, 'stop')}
                  >
                    Stop
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    disabled={projectBusy || logsClearing}
                    onClick={() => void clearProjectLogs(project)}
                  >
                    Clear logs
                  </button>
                </div>
              </div>

              {open ? (
                <div className="table-wrap docker-project-containers">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Service</th>
                        <th>Container</th>
                        <th>Image</th>
                        <th>Status</th>
                        <th>Port</th>
                        <th>CPU</th>
                        <th>Memory</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {project.containers.map((c) => {
                        const id = String(c.name || c.short_id || c.id);
                        const busy = busyId === `${id}:start` || busyId === `${id}:stop` || busyId === `${id}:restart` || busyId === `${id}:pause` || busyId === `${id}:unpause` || projectBusy;
                        const running = isRunning(c.state || c.status);
                        const paused = String(c.state || '').toLowerCase() === 'paused';
                        const portLabel =
                          c.port != null
                            ? String(c.port)
                            : Array.isArray(c.ports) && c.ports.length
                              ? String(c.ports[0])
                              : '—';
                        return (
                          <tr key={containerKey(c)}>
                            <td>{c.service || '—'}</td>
                            <td>
                              {c.name || '—'}
                              {c.protected ? (
                                <span className="badge text-bg-secondary" style={{ marginLeft: '0.4rem' }}>
                                  protected
                                </span>
                              ) : null}
                            </td>
                            <td className="muted small">{c.image || '—'}</td>
                            <td>
                              <span
                                className={`badge ${running ? 'text-bg-success' : paused ? 'text-bg-warning' : 'text-bg-secondary'}`}
                              >
                                {c.state || c.status || '—'}
                              </span>
                            </td>
                            <td title={Array.isArray(c.ports) && c.ports.length > 1 ? c.ports.join(', ') : undefined}>
                              {portLabel}
                            </td>
                            <td>{c.cpu != null ? `${c.cpu}%` : '—'}</td>
                            <td>{formatBytes(c.memory)}</td>
                            <td>
                              <div className="row-actions" style={{ flexWrap: 'wrap' }}>
                                {running || paused ? (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary"
                                    disabled={busy || c.protected}
                                    onClick={() => void runAction(id, 'restart')}
                                  >
                                    Restart
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary"
                                    disabled={busy}
                                    onClick={() => void runAction(id, 'start')}
                                  >
                                    Start
                                  </button>
                                )}
                                {running ? (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-warning"
                                    disabled={busy || c.protected}
                                    onClick={() => void runAction(id, 'stop')}
                                  >
                                    Stop
                                  </button>
                                ) : null}
                                {paused ? (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    disabled={busy || c.protected}
                                    onClick={() => void runAction(id, 'unpause')}
                                  >
                                    Unpause
                                  </button>
                                ) : running ? (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    disabled={busy || c.protected}
                                    onClick={() => void runAction(id, 'pause')}
                                  >
                                    Pause
                                  </button>
                                ) : null}
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-secondary"
                                  disabled={busy}
                                  onClick={() => void openLogs(c)}
                                >
                                  Logs
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger"
                                  disabled={busy || logsClearing}
                                  onClick={() => void clearLogs(id)}
                                >
                                  Clear logs
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      {logsFor ? (
        <div className="card-panel" style={{ marginTop: '1.25rem' }}>
          <div className="d-flex justify-content-between align-items-center gap-2 flex-wrap">
            <h2 style={{ margin: 0, fontSize: '1.1rem' }}>Logs — {logsFor}</h2>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setLogsFor(null)}>
              Close
            </button>
          </div>
          <div className="pm2-logs-controls" style={{ marginTop: '0.75rem' }}>
            <label className="field" style={{ margin: 0 }}>
              <span className="small">Type</span>
              <SearchableSelect
                className="form-select form-select-sm pm2-logs-type"
                value={logsType}
                onChange={(e) => setLogsType(e.target.value)}
              >
                <option value="both">both</option>
                <option value="stdout">stdout</option>
                <option value="stderr">stderr</option>
              </SearchableSelect>
            </label>
            <label className="field" style={{ margin: 0 }}>
              <span className="small">Lines</span>
              <FormInput
                type="number"
                min={1}
                max={5000}
                value={logsLines}
                onChange={(e) => setLogsLines(Number(e.target.value) || 100)}
              />
            </label>
            <button
              type="button"
              className="btn btn-sm btn-primary pm2-logs-load"
              disabled={logsLoading}
              onClick={() => void refreshLogs()}
            >
              {logsLoading ? 'Loading…' : 'Load'}
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              disabled={logsLoading || logsClearing}
              onClick={() => void clearLogs(logsFor)}
            >
              {logsClearing ? 'Clearing…' : 'Clear logs'}
            </button>
          </div>
          <pre
            style={{
              marginTop: '0.75rem',
              maxHeight: '420px',
              overflow: 'auto',
              background: 'var(--bs-tertiary-bg, #f8f9fa)',
              padding: '0.75rem',
              borderRadius: '6px',
              fontSize: '0.8rem',
            }}
          >
            {logsLoading ? 'Loading…' : logsContent || 'No log content.'}
          </pre>
        </div>
      ) : null}
    </div>
  );
}
